/** Admin layout preferences, persisted in a cookie and read before the first render (no layout flash). */

export const PREFERENCES_COOKIE = "milqit_admin_prefs";
/** Written by the shadcn sidebar when it is collapsed or expanded. */
export const SIDEBAR_COOKIE = "sidebar_state";

export const SIDEBAR_VARIANTS = ["inset", "sidebar", "floating"] as const;
export const SIDEBAR_COLLAPSIBLES = ["icon", "offcanvas"] as const;
export const CONTENT_LAYOUTS = ["centered", "full-width"] as const;
export const NAVBAR_STYLES = ["sticky", "scroll"] as const;

export interface AdminPreferences {
  sidebarVariant: (typeof SIDEBAR_VARIANTS)[number];
  sidebarCollapsible: (typeof SIDEBAR_COLLAPSIBLES)[number];
  contentLayout: (typeof CONTENT_LAYOUTS)[number];
  navbarStyle: (typeof NAVBAR_STYLES)[number];
}

export const DEFAULT_PREFERENCES: AdminPreferences = {
  sidebarVariant: "inset",
  sidebarCollapsible: "icon",
  contentLayout: "centered",
  navbarStyle: "sticky",
};

const pick = <T extends readonly string[]>(
  options: T,
  value: unknown,
  fallback: T[number],
): T[number] =>
  typeof value === "string" && (options as readonly string[]).includes(value)
    ? (value as T[number])
    : fallback;

/** Parses the cookie value defensively — a bad cookie never breaks the layout. */
export function parsePreferences(raw: string | undefined): AdminPreferences {
  if (!raw) return DEFAULT_PREFERENCES;
  try {
    const value = JSON.parse(decodeURIComponent(raw)) as Partial<
      Record<keyof AdminPreferences, unknown>
    >;
    return {
      sidebarVariant: pick(
        SIDEBAR_VARIANTS,
        value.sidebarVariant,
        DEFAULT_PREFERENCES.sidebarVariant,
      ),
      sidebarCollapsible: pick(
        SIDEBAR_COLLAPSIBLES,
        value.sidebarCollapsible,
        DEFAULT_PREFERENCES.sidebarCollapsible,
      ),
      contentLayout: pick(CONTENT_LAYOUTS, value.contentLayout, DEFAULT_PREFERENCES.contentLayout),
      navbarStyle: pick(NAVBAR_STYLES, value.navbarStyle, DEFAULT_PREFERENCES.navbarStyle),
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

export function serializePreferences(preferences: AdminPreferences): string {
  return encodeURIComponent(JSON.stringify(preferences));
}

function readCookie(name: string): string | undefined {
  try {
    return document.cookie
      .split("; ")
      .find((entry) => entry.startsWith(`${name}=`))
      ?.slice(name.length + 1);
  } catch {
    return undefined;
  }
}

export const readPreferences = (): AdminPreferences =>
  parsePreferences(readCookie(PREFERENCES_COOKIE));

export const readSidebarOpen = (): boolean => readCookie(SIDEBAR_COOKIE) !== "false";
